import { useState, useContext, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

// Assets
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import editIcon from "../../assets/edit_icon.png";
import deleteIcon from "../../assets/delete_Icon.png";

// Utils
import { UserAuth } from "../../utils/AuthContext";
import { onlyNums } from "../../utils/OnlyNums";
import { formatDate } from "../../utils/toDate";

// Components
import Modal from "../../components/Modal";
import CustomInput from "../../components/CustomInput";
import SectionDivider from "../../components/SectionDivider";
import Dropdown from "../../components/Dropdown";
import DiscountSection from "../../components/DiscountSection";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import Loader from "../../components/Loader";
import CustomButton from "../../components/CustomButton";
import ViewEditDiv from "../../components/ViewEditDiv";
import TextDiv from "../../components/TextDiv";
import AreYouSureModal from "../../components/AreYouSureModal";
import { sanitizeNumber } from "../../utils/sanitizeNumber";

const EditAppointmentModal = ({
  appointment,
  setShowModal,
  allDoctors,
  getAllAppointments,
  onConfirm,
}) => {
  const { t } = useTranslation("general");

  const { userData } = useContext(UserAuth);

  // Temp state
  const [selectedCustomer, setSelectedCustomer] = useState({
    id: appointment?.patientId,
    name: appointment?.customerName,
  });

  // Temp state
  const [tempAppointment, setTempAppointment] = useState({
    date: appointment?.date,
    time: appointment?.time,
    notes: appointment?.notes,
  });

  // Temp treatment state
  const [errorFields, setErrorFields] = useState({});
  const [treatments, setTreatments] = useState([]);
  const [subTreatments, setSubTreatments] = useState([]);
  const [selectedTreatment, setSelectedTreatment] = useState({
    name: appointment?.treatmentName,
    id: null,
  });

  const [selectedSubTreatment, setSelectedSubTreatment] = useState({
    name: appointment?.subTreatmentName,
    id: null,
  });
  const [loadingModal, setLoadingModal] = useState(false);
  const [price, setPrice] = useState(appointment?.priceAfterDiscount);
  const [originalPrice, setOriginalPrice] = useState(appointment?.price);

  // doctor state
  const [selectedDoctor, setSelectedDoctor] = useState({
    name: appointment?.doctorName,
  });

  const [tempDiscountDetails, setTempDiscountDetails] = useState({
    discountAmount: appointment?.discountAmount,
    discountType: appointment?.discountType,
  });

  const [editMode, setEditMode] = useState(false);
  const [showAreYouSure, setShowAreYouSure] = useState(false);

  const doctorAndHealer = ["Doctor", "Healer"];

  useEffect(() => {
    if (!treatments?.length) {
      getAllTreatments();
    }
  }, []);

  const validateFields = () => {
    let errors = {};
    let hasError = false;

    // Validate Customer Name
    if (!selectedCustomer?.name.trim()) {
      errors["name"] = t("emptyInputField");
      hasError = true;
    }

    // Validate Doctor Name
    if (!selectedDoctor) {
      errors["doctorName"] = t("doctorNameRequired");
      hasError = true;
    }

    // Validate Medical Treatment
    if (!selectedTreatment) {
      errors["medicalTreatmentName"] = t("medicalTreatmentRequired");
      hasError = true;
    }

    // Validate Price
    if (!parseFloat(sanitizeNumber(price))) {
      errors["price"] = t("validPriceRequired");
      hasError = true;
    }

    // Validate Appointment Date
    if (!tempAppointment.date) {
      errors["date"] = t("validDateRequired");
      hasError = true;
    }

    // Validate Appointment Time
    if (!tempAppointment.time) {
      errors["time"] = t("validTimeRequired");
      hasError = true;
    }

    setErrorFields(errors);
    return !hasError;
  };

  const handleCancelModal = () => {
    setTempAppointment({});
    setShowModal(false);
  };

  const handlePriceChange = (e) => {
    const priceValue = e.target.value;
    setPrice(priceValue);
    setOriginalPrice(priceValue);
  };

  const handleInputChange = (e, state = "appointment") => {
    let { name, value } = e.target;

    if (state === "selectedCustomer") {
      setSelectedCustomer((prev) => ({ ...prev, [name]: value }));
    }

    setTempAppointment((prevAppointment) => ({
      ...prevAppointment,
      [name]: value,
    }));
  };

  const getAllTreatments = async (page, limit, searchTerm = "") => {
    try {
      const token = userData?.token;
      if (!token) {
        console.error("Token is missing, aborting request.");
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getAllMedicalTreatments`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: {
            page: page || 1,
            limit: limit ?? 10,
            search: searchTerm,
            organizationId: userData?.organizationId,
          },
        }
      );

      const treatmentsData = response.data.data;

      setTreatments(treatmentsData);

      const selectedAppointmentTreatment = treatmentsData?.find((treatment) => {
        return treatment.name === appointment.treatmentName;
      });

      setSubTreatments(selectedAppointmentTreatment?.subMedicalTreatment);
    } catch (error) {
      console.error(
        "Error fetching treatments:",
        error.response?.data || error
      );
    }
  };

  // appointment modal functions
  const handleConfirmEdit = async () => {
    if (!validateFields()) {
      console.error("Invalid input", errorFields);
      return;
    }

    try {
      setLoadingModal(true);
      const token = userData?.token;
      const userId = parseInt(userData.userId, 10);

      const organizationId = parseInt(userData?.organizationId);

      // if (!userId) {
      //   console.error("User ID not found in localStorage");
      //   return;
      // }

      const payload = {
        userId,
        organizationId,
        doctorName: selectedDoctor.name,
        ...tempAppointment,
        treatmentName: selectedTreatment?.name,
        subTreatmentName: selectedSubTreatment?.name,
        price: parseFloat(sanitizeNumber(originalPrice)),
        priceAfterDiscount: parseFloat(sanitizeNumber(price)),
        discountType: tempDiscountDetails?.discountType,
        discountAmount: sanitizeNumber(tempDiscountDetails?.discountAmount),
      };

      const response = await axios.patch(
        `${process.env.REACT_APP_URL}/editAppointment/${appointment?.id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status == 200) {
        setShowModal(false);

        if (onConfirm) {
          onConfirm(response);
        } else {
          getAllAppointments();
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingModal(false);
    }
  };

  const handleAttemptEdit = () => {
    setEditMode((prev) => !prev);
  };

  const handleCancelEdit = () => {
    setEditMode((prev) => !prev);

    // * Reset states to defaults
    setSelectedDoctor({
      name: appointment?.doctorName,
    });
    setTempAppointment({
      date: appointment?.date,
      time: appointment?.time,
      notes: appointment?.notes,
    });
    // console.log("appointment?.treatmentName", appointment);
    setSelectedTreatment({
      name: appointment?.treatmentName,
      id: null,
    });
    setSelectedSubTreatment({
      name: appointment?.subTreatmentName,
      id: null,
    });
    setPrice(appointment?.priceAfterDiscount);
    setOriginalPrice(appointment?.price);
  };

  const deleteAppointment = async () => {
    try {
      const token = userData?.token;
      if (!token) return;

      const response = await axios.delete(
        `${process.env.REACT_APP_URL}/deleteAppointment`,
        {
          data: { id: appointment?.id },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 204) {
        console.log("Appointment deleted");
      }
    } catch (error) {
      console.error("Error deleting appointment:", error);
    }
  };

  const handleDeleteAppointment = async () => {
    await deleteAppointment();
    await getAllAppointments();
    setShowAreYouSure(false);
    setShowModal(false);
  };

  const viewEditDivStyles =
    "o-row o-row--vertical-align_center o-row--gap-size_500 ";
  const viewEditPStyles = "t-bold_normal u-width-specific_150 u-margin_0";

  return (
    <>
      <Modal
        title={editMode ? "editAppointment" : "appointmentDetails"}
        onClose={handleCancelModal}
        footer={
          <>
            {loadingModal ? <Loader /> : null}
            <section className="o-row o-row--horizontal-align_end o-row--gap-size_300">
              {editMode ? (
                <>
                  <CustomButton
                    title={t("cancel")}
                    onClick={handleCancelEdit}
                    variant="blank"
                    icon={closeIcon}
                    iconSize={14}
                  />
                  <CustomButton
                    onClick={handleConfirmEdit}
                    title={t("confirm")}
                    icon={saveIcon}
                    iconSize={16}
                    disabled={loadingModal}
                  />
                </>
              ) : (
                <>
                  {doctorAndHealer.includes(userData?.role) ? (
                    <>
                      {formatDate(appointment?.date) ===
                      formatDate(new Date()) ? (
                        <>
                          <CustomButton
                            title={t("delete")}
                            onClick={() => setShowAreYouSure(true)}
                            variant="red"
                            icon={deleteIcon}
                            iconSize={18}
                          />
                          <CustomButton
                            onClick={handleAttemptEdit}
                            title={t("edit")}
                            icon={editIcon}
                            iconSize={18}
                            disabled={loadingModal}
                            variant="blank"
                          />
                        </>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <CustomButton
                        title={t("delete")}
                        onClick={() => setShowAreYouSure(true)}
                        variant="red"
                        icon={deleteIcon}
                        iconSize={18}
                      />
                      <CustomButton
                        onClick={handleAttemptEdit}
                        title={t("edit")}
                        icon={editIcon}
                        iconSize={18}
                        disabled={loadingModal}
                        variant="blank"
                      />
                    </>
                  )}
                </>
              )}
            </section>
          </>
        }
      >
        <div className="o-column o-column--gap-size_400">
          {/* patient details */}
          <SectionDivider title={"patientDetails"} />

          <ViewEditDiv
            title={t("patientName")}
            value={selectedCustomer?.name}
            divClasses={viewEditDivStyles}
            pClasses={viewEditPStyles}
          />

          {/* appointment details */}
          <SectionDivider title={"appointmentDetails"} />

          <ViewEditDiv
            title={t("healerName")}
            value={selectedDoctor?.name}
            divClasses={viewEditDivStyles}
            pClasses={viewEditPStyles}
            editMode={editMode}
            InputElement={
              <Dropdown
                options={allDoctors}
                selected={selectedDoctor}
                setSelected={(value) => {
                  setSelectedDoctor(value);
                }}
                flex
                errorText={errorFields["doctorName"]}
              />
            }
          />

          <ViewEditDiv
            title={t("Date")}
            value={formatDate(tempAppointment?.date)}
            divClasses={viewEditDivStyles}
            pClasses={viewEditPStyles}
            editMode={editMode}
            InputElement={
              <CustomInput
                name="date"
                type="date"
                flex={true}
                value={formatDate(tempAppointment?.date)}
                onChange={handleInputChange}
                errorText={errorFields["date"]}
              />
            }
          />

          <ViewEditDiv
            title={t("treatmentType")}
            value={selectedTreatment?.name}
            divClasses={viewEditDivStyles}
            pClasses={viewEditPStyles}
            editMode={editMode}
            InputElement={
              <Dropdown
                options={treatments}
                selected={selectedTreatment}
                setSelected={(value) => {
                  setSelectedTreatment(value);
                  setSubTreatments(value.subMedicalTreatment);
                  setSelectedSubTreatment(null);
                  setPrice(value?.treatmentPrice || 0);
                  const newPrice = value?.treatmentPrice || 0;
                  setOriginalPrice(newPrice);
                }}
                flex
                errorText={errorFields["medicalTreatmentName"]}
              />
            }
          />

          {editMode ? (
            <>
              {subTreatments?.length > 0 && (
                <div className={viewEditDivStyles}>
                  <p className={viewEditPStyles}>{t("subTreatmentType")}:</p>
                  <Dropdown
                    options={subTreatments}
                    selected={selectedSubTreatment}
                    setSelected={(value) => {
                      setSelectedSubTreatment(value);
                      const newPrice = value.price;
                      setOriginalPrice(newPrice);
                      setPrice(newPrice);
                    }}
                    flex
                  />
                </div>
              )}
            </>
          ) : (
            <>
              {selectedSubTreatment?.name && (
                <TextDiv
                  title={t("subTreatmentType")}
                  value={selectedSubTreatment?.name}
                  divClasses={viewEditDivStyles}
                  pClasses={viewEditPStyles}
                />
              )}
            </>
          )}

          <ViewEditDiv
            title={t("price")}
            value={`${originalPrice} ${t("IQD")}`}
            divClasses={viewEditDivStyles}
            pClasses={viewEditPStyles}
            editMode={editMode}
            InputElement={
              <CustomInputWithUnit
                name="price"
                value={originalPrice}
                onChange={handlePriceChange}
                onKeyDown={onlyNums}
                flex={true}
                unit="IQD"
                errorText={errorFields["price"]}
              />
            }
          />

          {editMode ? (
            <section>
              <DiscountSection
                originalPrice={originalPrice}
                finalPrice={price}
                onPriceChange={(priceValue, discountType, discountAmount) => {
                  setPrice(priceValue);
                  setTempDiscountDetails({
                    discountAmount,
                    discountType,
                  });
                }}
                initialDiscountType={tempDiscountDetails?.discountType}
                initialDiscountAmount={tempDiscountDetails?.discountAmount}
              />
            </section>
          ) : (
            <>
              <TextDiv
                title="priceAfterDiscount"
                value={`${price} ${t("IQD")}`}
                divClasses={viewEditDivStyles}
                pClasses={viewEditPStyles}
              />

              {appointment?.discountAmount ? (
                <TextDiv
                  title="discount"
                  value={`${appointment?.discountAmount} ${
                    appointment?.discountType === "percentage" ? "%" : t("IQD")
                  }`}
                  divClasses={viewEditDivStyles}
                  pClasses={viewEditPStyles}
                />
              ) : (
                <TextDiv
                  title="discount"
                  value={t("noDiscount")}
                  divClasses={viewEditDivStyles}
                  pClasses={viewEditPStyles}
                />
              )}
            </>
          )}

          <ViewEditDiv
            title={t("time")}
            value={tempAppointment?.time}
            divClasses={viewEditDivStyles}
            pClasses={viewEditPStyles}
            editMode={editMode}
            InputElement={
              <CustomInput
                name="time"
                type="time"
                flex={true}
                value={tempAppointment?.time}
                onChange={handleInputChange}
                errorText={errorFields["time"]}
              />
            }
          />

          <ViewEditDiv
            title={t("notes")}
            value={tempAppointment?.notes}
            divClasses={viewEditDivStyles}
            pClasses={viewEditPStyles}
            editMode={editMode}
            InputElement={
              <CustomInput
                name="notes"
                type="textarea"
                flex={true}
                value={tempAppointment?.notes}
                onChange={handleInputChange}
              />
            }
          />
        </div>
      </Modal>

      {showAreYouSure && (
        <AreYouSureModal
          onClose={() => setShowAreYouSure(false)}
          onConfirm={handleDeleteAppointment}
          customMessage="areYouSureYouWantToDelete"
          closeTitle="noDontDelete"
          confirmTitle="yesDelete"
        />
      )}
    </>
  );
};

export default EditAppointmentModal;
